import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, map, of, startWith } from 'rxjs';
import { DialerTypes } from 'src/app/models/centrex/enums/dialer-types';
import { ExtensionTypes } from 'src/app/models/centrex/enums/extension-types';
import { Extension } from 'src/app/models/centrex/extension';
import { Site } from 'src/app/models/centrex/site';
import { User } from 'src/app/models/user';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { SiteService } from 'src/app/services/centrex/site.service';
import { OrganizationsService } from 'src/app/services/organizations.service';
import { UsersService } from 'src/app/services/users.service';
import { DialogAddExtensionsComponent } from '../../dialogs/dialog-add-extensions/dialog-add-extensions.component';
import { ServiceService } from 'src/app/services/centrex/service.service';
import { Service } from 'src/app/models/centrex/service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { Instance } from 'src/app/models/centrex/instance';
import { TranslateDatePipe } from '../../../pipes/translate-date.pipe';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatList, MatListItem } from '@angular/material/list';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckbox } from '@angular/material/checkbox';
import { RegistrationStates } from 'src/app/models/centrex/enums/registration-states';
import { MatTableModule } from '@angular/material/table';
import { Contact } from 'src/app/models/centrex/contact';

@Component({
    selector: 'app-extension-form',
    templateUrl: './extension-form.component.html',
    styleUrls: ['./extension-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatCardModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatSelect,
        MatOption,
        MatAutocompleteTrigger,
        MatAutocomplete,
        MatChipListbox,
        CdkDropList,
        MatChipOption,
        CdkDrag,
        MatChipRemove,
        MatIconButton,
        MatSuffix,
        MatSlideToggle,
        MatButton,
        RouterLink,
        AsyncPipe,
        TranslateModule,
        TranslateDatePipe,
        MatCheckbox,
        MatIconModule,
        MatTableModule,
    ],
})
export class ExtensionFormComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    extensionForm: UntypedFormGroup;
    isCreation: boolean;
    sites: Site[] = [];
    users: User[] = [];
    extensions: Extension[] = [];
    services: Service[] = [];
    instance: Instance = null;

    addExtensionsDialog: MatDialogRef<DialogAddExtensionsComponent>;

    extensionTypes = Object.keys(ExtensionTypes);
    dialerTypes = Object.keys(DialerTypes);
    registrationStates = RegistrationStates;

    filteredUsers: Observable<User[]>;

    private _extension: Extension;

    @Input() set extension(value: Extension) {
        if (value) {
            this._extension = value;
        }
    }

    get extension(): Extension {
        return this._extension;
    }

    private _extensionsList: Extension[] = null;

    @Input() set extensionsList(value: Extension[]) {
        if (value) {
            this._extensionsList = value;
        }
    }

    get extensionsList(): Extension[] {
        return this._extensionsList;
    }

    displayedColumns: string[] = ['networkAddress', 'sipAddress', 'userAgent', 'contactAddress'];
    dataSource?: Contact[];

    constructor(
        private router: Router,
        private dialog: MatDialog,
        private extensionService: ExtensionService,
        private siteService: SiteService,
        private userService: UsersService,
        private organizationsService: OrganizationsService,
        private instanceService: InstanceService,
        private serviceService: ServiceService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService
    ) {
        this.createForm({} as Extension);
    }

    ngOnInit(): void {
        this.loadDatas(this.extension);
    }

    loadDatas(extension: Extension) {
        this.spinner.show();

        if (extension?.id) {
            this.isCreation = false;

            let datas: Observable<[User[], Site[], Extension[], Service[], Instance]>;

            if (!this.extensionsList) {
                datas = forkJoin([
                    this.userService.getUsers(),
                    this.siteService.getSites(),
                    this.extensionService.getExtensions(),
                    this.serviceService.getServices(),
                    this.instanceService.getInstance(),
                ]);
            } else {
                datas = forkJoin([
                    this.userService.getUsers(),
                    this.siteService.getSites(),
                    of(this.extensionsList),
                    this.serviceService.getServices(),
                    this.instanceService.getInstance(),
                ]);

                console.log('extension-form: OK');
            }

            datas.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: ([users, sites, extensions, services, instance]) => {
                    this.users = users;
                    this.sites = sites;
                    this.extensions = extensions.filter((ext) => ext.type === ExtensionTypes.user);
                    this.services = services;
                    this.instance = instance;

                    this.extension.features ??= this.instance?.features;

                    // Créé le formulaire
                    this.createForm(this.extension);
                    this.dataSource = this.extension.contacts;
                },
                error: () => {
                    this.createForm(this.extension);
                    this.dataSource = this.extension.contacts;
                    this.spinner.hide();
                },
            });
        } else {
            this.isCreation = true;
            this._extension = new Extension();
            this.extension.organizationId = this.organizationsService.getCurrentOrganization()?.id ?? '';
            this.extension.type = ExtensionTypes.user;

            forkJoin([
                this.userService.getUsers(),
                this.siteService.getSites(),
                this.extensionService.getExtensions(),
                this.serviceService.getServices(),
                this.instanceService.getInstance(),
            ])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: ([users, sites, extensions, services, instance]) => {
                        this.users = users;
                        this.sites = sites;
                        this.extensions = extensions.filter((ext) => ext.type === ExtensionTypes.user);
                        this.services = services;

                        this.extension.features ??= this.instance?.features;

                        // Créé le formulaire
                        this.createForm(this.extension);
                        this.dataSource = this.extension.contacts;
                    },
                    error: () => {
                        this.createForm(this.extension);
                        this.dataSource = this.extension.contacts;
                        this.spinner.hide();
                    },
                });
        }
    }

    createForm(extension: Extension) {
        this.extensionForm = this.formbuilder.group({
            id: [extension.id],
            organizationId: [extension.organizationId],
            name: [extension.name, Validators.required],
            type: [
                {
                    value: extension.type,
                    disabled: extension.type === ExtensionTypes.device,
                },
            ],
            userId: [extension.userId],
            siteId: [
                {
                    value: extension.siteId,
                    disabled: extension.type === ExtensionTypes.device,
                },
            ],
            service: [extension.service],
            number: [
                extension.number,
                { validators: [Validators.required, extension.type !== ExtensionTypes.device ? Validators.pattern(/^\d{2,4}$/) : Validators.nullValidator] },
            ],
            externalNumber: [extension.externalNumber, Validators.pattern(/[0-9\+\-\ ]/)],
            account: [extension.account],
            password: [''],
            allowedNetwork: [extension.allowedNetwork],
            extensions: [extension.extensions],
            dialerType: [extension.dialerType],
            registrationState: [extension.registrationState],
            activeState: [extension.activeState],
            feature_VM: [{ value: Object.keys(extension.features ?? {}).includes('VM'), disabled: extension.type === ExtensionTypes.device }],
            feature_VM_MAIL: [{ value: Object.keys(extension.features ?? {}).includes('VM_MAIL'), disabled: extension.type === ExtensionTypes.device }],
            feature_VM_SMS: [{ value: Object.keys(extension.features ?? {}).includes('VM_SMS'), disabled: extension.type === ExtensionTypes.device }],
            feature_VM_TRANSCRIPT: [{ value: Object.keys(extension.features ?? {}).includes('VM_TRANSCRIPT'), disabled: extension.type === ExtensionTypes.device }],
            allowInbound: [{ value: extension.allowInbound, disabled: false }],
            allowOutbound: [{ value: extension.allowOutbound, disabled: false }],
        });

        // Si type utilisateur, ajout des validators
        if (extension.type === ExtensionTypes.user) {
            this.extensionForm.get('userId').setValidators([Validators.required, this.autocompleteUserValidator()]);
            this.extensionForm.get('account').setValidators(Validators.required);

            if (this.isCreation) {
                this.extensionForm.get('password').setValidators(Validators.required);
            }
        }

        this.extensionForm
            .get('type')
            .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                // Initialise tous les champs des différents types d'extensions
                this.extensionForm.get('userId').setValue('');
                this.extensionForm.get('account').setValue('');
                this.extensionForm.get('password').setValue('');
                this.extensionForm.get('dialerType').setValue(DialerTypes.none);
                this.extensionForm.get('extensions').setValue([]);
                this.extensionForm.get('service').setValue('');
                this.extensionForm.get('externalNumber').setValue('');

                // Met à jour les validators de userId en fonction du type
                if (value === ExtensionTypes.user) {
                    this.extensionForm.get('userId').setValidators([Validators.required, this.autocompleteUserValidator()]);
                    this.extensionForm.get('account').setValidators(Validators.required);

                    if (this.isCreation) {
                        this.extensionForm.get('password').setValidators(Validators.required);
                    }
                } else {
                    this.extensionForm.get('userId').clearValidators();
                    this.extensionForm.get('account').clearValidators();
                    this.extensionForm.get('password').clearValidators();
                }

                if (value === ExtensionTypes.group) {
                    this.extensionForm.get('dialerType').setValue(DialerTypes.none);
                }

                if (value === ExtensionTypes.service) {
                    this.extensionForm.get('service').setValidators(Validators.required);
                } else {
                    this.extensionForm.get('service').clearValidators();
                }

                this.extensionForm.get('userId').updateValueAndValidity();
                this.extensionForm.get('account').updateValueAndValidity();
                this.extensionForm.get('password').updateValueAndValidity();
                this.extensionForm.get('service').updateValueAndValidity();
            });

        this.filteredUsers = this.extensionForm.get('userId').valueChanges.pipe(
            startWith<string | User>(''),
            map((value) => {
                const name = typeof value === 'string' ? value : value.account;
                return name ? this.filterUsers(name as string) : this.users.slice();
            })
        );

        this.featureSelectionChange();

        this.spinner.hide();
    }

    onSubmit() {
        this.spinner.show();

        this.commitChanges();

        if (this.isCreation) {
            // Appel de la méthode de création
            this.extensionService
                .addExtension(this.extension)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (extensionCreated) => {
                        this.toastr.success(this.translate.instant('Extension.SuccessMessageCreateExtension'));

                        // Redirige sur la page des extensions
                        this.router.navigate(['/extensions']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Extension.ErrorMessageCreateExtension'));

                        this.spinner.hide();
                    },
                });
        } else {
            this.extensionService
                .updateExtension(this.extension)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (extension) => {
                        this.toastr.success(this.translate.instant('Extension.SuccessMessageUpdateExtension'));

                        this.router.navigate(['/extensions']);

                        this.spinner.hide();
                    },
                    error: () => {
                        this.toastr.error(this.translate.instant('Extension.ErrorMessageUpdateExtension'));

                        this.spinner.hide();
                    },
                });
        }
    }

    commitChanges() {
        this.extension.name = this.extensionForm.value.name;
        this.extension.type = this.extensionForm.value.type;
        this.extension.userId = this.extensionForm.value.userId;
        this.extension.siteId = this.extensionForm.value.siteId;
        this.extension.service = this.extensionForm.value.service;
        this.extension.number = this.extensionForm.value.number;
        this.extension.externalNumber = this.extensionForm.value.externalNumber;
        this.extension.account = this.extensionForm.value.account;
        if (this.isCreation) {
            this.extension.password = this.extensionForm.value.password;
        }
        this.extension.allowedNetwork = this.extensionForm.value.allowedNetwork;
        this.extension.extensions = this.extensionForm.value.extensions;
        this.extension.dialerType = this.extensionForm.value.dialerType;
        this.extension.registrationState = this.extensionForm.value.registrationState;
        this.extension.activeState = this.extensionForm.value.activeState;
        this.extension.features = new Object();
        if (this.extensionForm.controls['feature_VM'].value) this.extension.features['VM'] = '';
        if (this.extensionForm.controls['feature_VM_MAIL'].value) this.extension.features['VM_MAIL'] = '';
        if (this.extensionForm.controls['feature_VM_SMS'].value) this.extension.features['VM_SMS'] = '';
        if (this.extensionForm.controls['feature_VM_TRANSCRIPT'].value) this.extension.features['VM_TRANSCRIPT'] = '';
        this.extension.allowInbound = this.extensionForm.value.allowInbound;
        this.extension.allowOutbound = this.extensionForm.value.allowOutbound;
    }

    getExtensionDisplay(extensionId: string): string {
        const extension = this.extensions.find((a) => a.id === extensionId);
        return extension ? `${extension?.number} - ${extension?.name}` : extensionId;
    }

    drop(event: CdkDragDrop<Extension[]>) {
        moveItemInArray(this.extensionForm.value.extensions, event.previousIndex, event.currentIndex);
    }

    addExtensions() {
        // Ouverture de la popup de sélection des extensions
        this.addExtensionsDialog = this.dialog.open(DialogAddExtensionsComponent, {
            disableClose: true,
            // Liste des extensions sans les extensions déjà sélectionnées
            data: this.extensions.filter((a) => this.extensionForm.value.extensions?.findIndex((b) => b === a.id) < 0),
        });

        this.addExtensionsDialog
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result: Extension[]) => {
                if (result !== null) {
                    result.forEach((x) => {
                        this.extensionForm.get('extensions').value.push(x.id);
                    });
                }
            });
    }

    removeExtension(extensionSelectedId: string) {
        this.extensionForm.get('extensions').value.splice(this.extensionForm.get('extensions').value.indexOf(extensionSelectedId), 1);
    }

    displayUser = (userId: string): string => {
        return userId ? this.users.find((x) => x.id === userId)?.account : '';
    };

    userSelectionChange(event: MatAutocompleteSelectedEvent) {
        let user = event.option.value as User;
        if (user) {
            this.extensionForm.get('userId').setValue(user);
        }
    }

    featureSelectionChange() {
        if (!this.extensionForm.get('feature_VM').value) {
            this.extensionForm.get('feature_VM_MAIL')?.disable();
            this.extensionForm.get('feature_VM_SMS')?.disable();
        } else {
            this.extensionForm.get('feature_VM_MAIL')?.enable();
            this.extensionForm.get('feature_VM_SMS')?.enable();
        }

        if (!this.extensionForm.get('feature_VM').value || (!this.extensionForm.get('feature_VM_MAIL').value && !this.extensionForm.get('feature_VM_SMS').value)) {
            this.extensionForm.get('feature_VM_TRANSCRIPT')?.disable();
        } else {
            this.extensionForm.get('feature_VM_TRANSCRIPT')?.enable();
        }
    }

    private filterUsers(value: string): User[] {
        const filterValue = value.toLowerCase();

        return this.users.filter((a) => a.account.toLowerCase().includes(filterValue));
    }

    private autocompleteUserValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: boolean } | null => {
            let validation: { [key: string]: boolean } = {};

            if (this.users.findIndex((a) => a.id === control.value) === -1) {
                validation['invalidSelection'] = true;
                return validation;
            }

            return null;
        };
    }
}
